// src/config/symptomsConfig.ts
import type {
    SymptomKey,
    SymptomRiskLevel,
    BleedingColour,
    BleedingFlow,
    ClotLevel,
    PerinealPainLevel,
    WoundPainLevel,
    FatigueLevel,
    BreastfeedingPainLevel,
    BladderControlLevel,
    BowelFunctionLevel,
    PelvicHeavinessLevel,
    PainAndMovementLevel,
  } from "../types/recovery";
  
  type OptionValue = string;
  
  export interface SymptomOption<T extends OptionValue> {
    value: T;
    label: string;
    risk?: SymptomRiskLevel;
    dotSizePx?: number;
    description?: string;
  }
  
  export interface SymptomConfig<T extends OptionValue> {
    key: SymptomKey;
    label: string;
    question: string;
    themeWeeks: number[]; // which weeks this is emphasised in
    options: SymptomOption<T>[];
  }
  
  // Bleeding colour + clots are handled via custom UI but still use config:
  
  export const vaginalBleedingColourOptions: SymptomOption<BleedingColour>[] = [
    { value: "bright_red", label: "Bright red", risk: "alert" },
    { value: "dark_red", label: "Dark red", risk: "monitor" },
    { value: "brown", label: "Brown", risk: "normal" },
    { value: "yellowish", label: "Yellowish", risk: "normal" },
    { value: "clear", label: "Clear", risk: "normal" },
  ];
  
  export const vaginalBleedingFlowOptions: SymptomOption<BleedingFlow>[] = [
    { value: "none", label: "No bleeding", risk: "normal" },
    { value: "mild", label: "Light", risk: "normal" },
    { value: "moderate", label: "Moderate", risk: "monitor" },
    { value: "heavy", label: "Heavy", risk: "alert" },
  ];
  
  export const clotOptions: SymptomOption<ClotLevel>[] = [
  { value: "none", label: "No clots", risk: "normal", dotSizePx: 0, description: "No clots observed" },
  { value: "small", label: "Small clots", risk: "normal", dotSizePx: 10, description: "Smaller than a 10p coin" },
  { value: "medium", label: "Medium clots", risk: "monitor", dotSizePx: 20, description: "About the size of a 50p coin" },
  { value: "large", label: "Large clots", risk: "alert", dotSizePx: 30, description: "Larger than a 50p coin" },
 
];
  
  // Other symptoms (chips)
  
  export const symptomConfigs = {
    perinealPain: {
      key: "perinealPain" as const,
      label: "Perineal pain or discomfort",
      question: "Do you feel discomfort or pain in the perineal area today?",
      themeWeeks: [1],
      options: [
        { value: "none", label: "None", risk: "normal" },
        { value: "mild", label: "Mild", risk: "normal" },
        { value: "moderate", label: "Moderate", risk: "monitor" },
        { value: "severe", label: "Severe", risk: "alert" },
      ] as SymptomOption<PerinealPainLevel>[],
    },
    cSectionWound: {
      key: "cSectionWound" as const,
      label: "C-section wound discomfort",
      question: "Any discomfort or concern around your wound area today?",
      themeWeeks: [1],
      options: [
        { value: "none", label: "None", risk: "normal" },
        { value: "mild", label: "Mild", risk: "normal" },
        { value: "moderate", label: "Moderate", risk: "monitor" },
        { value: "severe", label: "Severe", risk: "alert" },
      ] as SymptomOption<WoundPainLevel>[],
    },
    fatigue: {
      key: "fatigue" as const,
      label: "Fatigue / sleep quality",
      question: "How rested do you feel today?",
      themeWeeks: [1, 2, 3, 4, 5, 6],
      options: [
        { value: "mild", label: "Well rested", risk: "normal" },
        { value: "moderate", label: "A bit tired", risk: "monitor" },
        { value: "severe", label: "Exhausted", risk: "monitor" }, // escalation done across days
      ] as SymptomOption<FatigueLevel>[],
    },
    breastfeedingPain: {
      key: "breastfeedingPain" as const,
      label: "Breastfeeding pain or discomfort",
      question: "Any pain or discomfort while feeding today?",
      themeWeeks: [2],
      options: [
        { value: "none", label: "None", risk: "normal" },
        { value: "moderate", label: "Some discomfort", risk: "monitor" },
        { value: "severe", label: "Severe pain", risk: "alert" },
      ] as SymptomOption<BreastfeedingPainLevel>[],
    },
    bladderControl: {
      key: "bladderControl" as const,
      label: "Bladder control / leaks",
      question: "Any leaks or bladder discomfort today?",
      themeWeeks: [4],
      options: [
        { value: "none", label: "No leaks", risk: "normal" },
        { value: "mild", label: "Once", risk: "monitor" },
        { value: "moderate", label: "Often", risk: "alert" },
        { value: "severe", label: "Always", risk: "alert" },
      ] as SymptomOption<BladderControlLevel>[],
    },
    bowelFunction: {
      key: "bowelFunction" as const,
      label: "Bowel function",
      question: "Are you having trouble opening your bowels today?",
      themeWeeks: [4],
      options: [
        { value: "mild", label: "No trouble", risk: "normal" },
        {
          value: "moderate",
          label: "A bit constipated",
          risk: "monitor",
        },
        {
          value: "severe",
          label: "Very constipated",
          risk: "alert",
        },
      ] as SymptomOption<BowelFunctionLevel>[],
    },
    pelvicHeaviness: {
      key: "pelvicHeaviness" as const,
      label: "Pelvic heaviness",
      question: "Do you feel a heaviness or pressure in your pelvis today?",
      themeWeeks: [2, 3, 4, 5, 6],
      options: [
        { value: "none", label: "None", risk: "normal" },
        { value: "mild", label: "Mild", risk: "monitor" },
        { value: "moderate", label: "Moderate", risk: "monitor" },
        { value: "severe", label: "Severe", risk: "alert" },
      ] as SymptomOption<PelvicHeavinessLevel>[],
    },
    painAndMovement: {
      key: "painAndMovement" as const,
      label: "Pain and movement",
      question: "Is pain limiting your movement today?",
      themeWeeks: [3, 4, 5, 6],
      options: [
        { value: "none", label: "No limiting pain", risk: "normal" },
        { value: "mild", label: "Some limitation", risk: "monitor" },
        { value: "severe", label: "A lot", risk: "alert" },
      ] as SymptomOption<PainAndMovementLevel>[],
    },
  } as const;
  